/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AbstractDestroyable, Arr, assert, Callback1, Destructor, DoubleIndex, Logger, SingleIndex, VoidCallback1 } from '@deqo/foundation';
import {
    EventSink,
    ModelEntityInfo,
    ModelEvents,
    ModelMutation,
    ModelMutator,
    ModelOperation,
    PodSessionWithModelFeature,
    PodSessionWithPresenceFeature,
} from '@deqo/podopolis';
import { interfaces } from 'inversify';
import ReactDOM from 'react-dom';
import { AnimateHack } from '../../app/canvas/visualization/AnimateHack';
import { Resolver } from '../../app/Container';
import { assertStr, invokeLater, uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { Monitor } from '../../platform/Monitor';
import { AbstractObject } from './AbstractObject';
import { Component } from './Component';
import { ComponentSelector } from './ComponentSelector';
import { ComponentTuple } from './ComponentTuple';
import { Convention } from './Convention';
import { InterceptorBuilder, InterceptorBuilderInitial } from './Interceptor';
import { InterceptorRegister } from './InterceptorRegister';
import { ComponentType } from './Interfaces';
import { MemoStore } from './MemoStore';
import { ObjectHost } from './ObjectHost';
import { ObjectManagerBackend, ObjectManagerCreateNotAllowedError } from './ObjectManagerBackend';
import * as props from './ObjectSymbols';
import { RelationManager } from './RelationManager';
import { TriggerBuilder, TriggerBuilderInitial, TriggerOptions } from './Trigger';
import { TriggerCycle } from './TriggerCycle';
import { TriggerTree } from './TriggerTree';

const { $cappend, $cset, $ctruncate, $xcreate, $xdelete, $xrestore } = ModelOperation.codes;

const objLog = Logger.get('app.objects');
const errLog = Logger.get('app.errors');

interface OMCLS {
    'allocated': [Component];
    'affected': [Component];
    'released': [Component];
}

interface OMTLS {
    'updated': Component[];
}

export type ObjectPodSession = PodSessionWithModelFeature & PodSessionWithPresenceFeature;

export class ObjectManager extends AbstractDestroyable {

    private readonly subscriptions = new DoubleIndex<string, EventSink<any, any>>();

    private host = null as ObjectHostImpl;
    private objects = new Map<string, any>();
    private deadObjects = new Map<string, any>();
    private indexByType = new SingleIndex<ComponentType<any>, any>();
    private indexByConvention = new SingleIndex<Convention<any>, any>();
    private triggers = new TriggerTree();
    private relations = new RelationManager(id => this.objects.get(id));
    private interceptors = new InterceptorRegister();
    private activeUpdate: ObjectUpdateImpl | null = null;
    private activeCycle: TriggerCycle | null = null;

    constructor(resolver: Resolver, private backend: ObjectManagerBackend) {
        super();
        this.host = new ObjectHostImpl(this, this.interceptors, resolver, backend, c => this.affectObject(c));
        this.burden(() => this.subscriptions.clear());
        this.burden(backend.on('modelMutation', e => ReactDOM.unstable_batchedUpdates(() => this.onModelMutation(e))));
        this.burden(backend.on('modelOperation', this.onModelOperation.bind(this)));
        this.buildInitialState();

        (window as any).om = this;
    }

    public on<K extends keyof OMCLS>(target: 'component' | string, stream: K, sink: EventSink<OMCLS[K], K>): Destructor;
    public on<K extends keyof OMTLS>(stream: K, sink: EventSink<OMTLS[K], K>): Destructor;
    public on(...args: any[]): Destructor {
        if (args.length === 2) args.splice(0, 0, 'this');
        const [target, stream, sink] = args;
        this.guardDestroyed();
        this.subscriptions.add(target, stream, sink);
        return () => this.off(target, stream, sink);
    }

    public off<K extends keyof OMCLS>(target: 'component' | string, stream: K, sink: EventSink<OMCLS[K], K>): void;
    public off<K extends keyof OMTLS>(stream: K, sink: EventSink<OMTLS[K], K>): void;
    public off(...args: any[]): void {
        if (args.length === 2) args.splice(0, 0, 'this');
        const [target, stream, sink] = args;
        this.subscriptions.remove(target, stream, sink);
    }

    public find(predicate: Callback1<boolean, Component>): Component | null;
    public find<T extends Component>(toc: ComponentType<T> | Convention<T>, predicate: Callback1<boolean, T>): T | null;
    public find(...input: any[]): any {
        const iterable = input.length === 1
            ? this.objects.values()
            : this.getAll(input[0]);
        const predicate = input[input.length - 1];
        for (const o of iterable) {
            if (o instanceof Component && o.alive && predicate(o)) {
                return o;
            }
        }
        return null;
    }

    public findAll(predicate: Callback1<boolean, Component>): Component[];
    public findAll<T extends Component>(toc: ComponentType<T> | Convention<T>, predicate: Callback1<boolean, T>): T[];
    public findAll(...input: any[]): any {
        const iterable = input.length === 1
            ? this.objects.values()
            : this.getAll(input[0]);
        const predicate = input[input.length - 1];
        const results = [] as Component[];
        for (const o of iterable) {
            if (o instanceof Component && o.alive && predicate(o)) {
                results.push(o);
            }
        }
        return results;
    }

    public createInterceptor(id: string): InterceptorBuilderInitial {
        return new InterceptorBuilder(id, f => {
            this.interceptors.add(f);
            return () => this.interceptors.remove(f);
        });
    }

    public deleteInterceptor(id: string): boolean {
        const f = this.interceptors.get(id);
        if (f) this.interceptors.remove(f);
        return !!f;
    }

    public hasInterceptor(id: string): boolean {
        return !!this.interceptors.get(id);
    }

    public createRelation<T extends Component, K extends keyof T>(
        type: ComponentType<T>,
        property: K | string,
        leftName: string,
        rightName: string) {
        this.relations.define(type, property, leftName, rightName);
        for (const inst of this.getAll(type)) {
            this.relations.invalidate(inst.id);
        }
        return () => this.relations.undefine(type, property);
    }

    public createTrigger(id: string, options?: TriggerOptions): TriggerBuilderInitial {
        options = options ?? { priority: false };
        return new TriggerBuilder(id, options.priority, r => {
            this.triggers.add(r);
            return () => this.triggers.remove(r);
        });
    }

    public deleteTrigger(id: string): boolean {
        const t = this.triggers.get(id);
        if (t) this.triggers.remove(t);
        return !!t;
    }

    public hasTrigger(id: string): boolean {
        return !!this.triggers.get(id);
    }

    public getActiveTriggerCycle() {
        return this.activeCycle;
    }

    public query(selector: ComponentSelector): Component[] {
        const results = [] as Component[];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const [id, o] of this.objects) {
            if (o instanceof Component && selector.match(o)) {
                results.push(o);
            }
        }
        return results;
    }

    public getUpdate(): ObjectUpdate | null {
        return this.activeUpdate || null;
    }

    public get(id: string): any;

    public get<T extends Component>(id: string, toc: ComponentType<T> | Convention<T>): T;

    public get(id: string, toc?: any): any {
        assertStr(id, 'id');
        const inst = this.objects.get(id);
        // If an instance exists validate and return
        if (inst) {
            // If type specified, validate the type
            if (toc) {
                assert(inst.is(toc), `ObjectManager.get: ${id} cannot be resolved as ${toc}.`);
            }
            return inst;
        }
        if (this.backend.getModel().exists(id)) {
            if (this.backend.shouldAutoAllocate()) {
                return this.allocateObject(id);
            }
            // Throw an error if auto allocate off (should be for live scenarios).
            else {
                throw new Error(`ObjectManager.get: ${id} not allocated but exists in model?`);
            }
        }
        else {
            throw new Error(`ObjectManager.get: ${id} does not exist.`);
        }
    }

    public getMaybe(id: string): Component | null;

    public getMaybe<T extends Component>(id: string, toc: ComponentType<T> | Convention<T>): T | null;

    public getMaybe(id: string, toc?: any): any {
        if (!!id && typeof (id) === 'string') {
            const inst = this.objects.get(id);
            if (inst && inst.alive && (!toc || inst.is(toc))) {
                return inst;
            }
        }
        return null;
    }

    public getMany(ids: string[]): Set<Component>;

    public getMany<T extends Component>(ids: string[], toc: ComponentType<T> | Convention<T>): Set<T>;

    public getMany(ids: string[], toc?: any): any {
        const results = new Set();
        for (const x of ids) {
            results.add(this.get(x, toc));
        }
        return results;
    }

    public getRelated(component: Component, relationName: string): readonly Component[] {
        if (!component.alive) return [];
        const ids = this.relations.query(component.id, relationName);
        return ids.map(x => this.getMaybe(x)).filter(x => x?.alive);
    }

    public has(id: string, toc?: any): boolean {
        const inst = this.objects.get(id);
        if (inst && (!toc || inst.is(toc))) {
            return true;
        }
        return false;
    }

    public touch(subjects: Component[]) {
        subjects = subjects.filter(x => x.alive);
        subjects.forEach(x => this.affectObject(x, true));
        this.pumpStream('this', 'updated', subjects);
    }

    public getRoot(): Component {
        return this.get('root', Component.describe('root').type);
    }

    public getAll(): Iterable<Component>;
    public getAll<T extends Component>(type: ComponentType<T>): ReadonlySet<T>;
    public getAll<T extends Component>(conv: Convention<T>): ReadonlySet<T>;
    public getAll(input?: any) {
        if (input) {
            return (input instanceof Convention)
                ? this.indexByConvention.get(input)
                : this.indexByType.get(input);
        }
        else {
            return this.objects.values();
        }
    }

    public update<T = void>(callback: Callback1<T, ObjectUpdate>): T {
        if (this.activeUpdate) {
            return this.invokeUpdateCallback(callback);
        }
        else {
            try {
                AnimateHack.animate = false;
                return this.beginOrJoinMutation(callback);
            }
            catch (e) {
                throw e;
            }
            finally {
                this.activeCycle = null;
                this.activeUpdate = null;
                invokeLater(0, () => AnimateHack.animate = true);
            }
        }
    }

    private beginOrJoinMutation<T = void>(callback: Callback1<T, ObjectUpdate>): T {
        let retVal!: T;
        // If lower level mutation already active, piggyback on that
        const em = this.backend.getMutation();
        if (em) {
            this.activeUpdate = this.activeUpdate = new ObjectUpdateImpl(this.get.bind(this), this.backend, em);
            retVal = this.invokeUpdateCallback(callback);
        }
        // Otherwise, start a new mutation
        else {
            this.backend.mutate(nm => {
                this.activeUpdate = new ObjectUpdateImpl(this.get.bind(this), this.backend, nm);
                this.activeUpdate.set('key', `unknown:${uuid().substr(0, 6)}`);
                retVal = this.invokeUpdateCallback(callback);
                return true;
            });
        }
        return retVal;
    }

    private buildInitialState(): void {
        const entityIds = this.backend.getModel().getList();
        for (const id of entityIds) {
            this.allocateObject(id);
        }
    }

    private invokeUpdateCallback<T = void>(callback: Callback1<T, ObjectUpdate>): T {
        assert(this.activeUpdate.depth < 50, 'ObjectManager: recursion limit reached on update.');
        // Start a new reaction cycle if we are not recursing or if we are in the reaction phase. Recursive mutation phase updates
        // should not start cycles because it will blur the lines between mutation and reaction.
        if (this.activeUpdate.depth === 0 || this.activeUpdate.phase === 'reaction') {
            this.activeCycle = new TriggerCycle(this, this.triggers);
        }
        this.activeUpdate.depth++;
        const rv = callback(this.activeUpdate);
        this.activeUpdate.depth--;
        if (this.activeUpdate.depth === 0) {
            this.activeUpdate.phase = 'reaction';
            this.activeCycle.run();
        }
        return rv;
    }

    private onModelMutation([_source, { applyBatch }]: ModelEvents['modelMutation']) {

        const done = new Set<string>();
        const updated = [] as Component[];
        for (const moop of applyBatch) {
            for (const id of ModelOperation.entities(moop)) {

                if (moop[0] === $cset) {
                    this.relations.notifyComponentSet(moop);
                }

                if (done.has(id)) continue;
                done.add(id);

                const obj = this.objects.get(id);
                // If object allocated
                if (obj) {
                    // If alive, affect
                    if (obj.alive) {
                        this.affectObject(obj);
                    }
                    // Otherwise, release
                    else {
                        this.releaseObject(obj, false);
                    }
                    updated.push(obj);
                }
                else {
                    const deadObj = this.deadObjects.get(id);
                    // If object is dead, just add to the updated list
                    if (deadObj) {
                        updated.push(deadObj);
                    }
                    // Otherwise, might need allocating
                    else if (this.backend.getModel().exists(id)) {
                        updated.push(this.allocateObject(id));
                    }
                    else {
                        // If we got here, it's probably an object that is locally deleted receiving a late update, so do nothing.
                    }
                }
            }
        }
        this.deadObjects.clear();
        this.pumpStream('this', 'updated', updated);
    }

    // @profileFrame('onUpdate')
    private onUpdate(writeSet: Iterable<string>): void {
        const updated = [] as Component[];
        for (const id of writeSet) {
            const obj = this.objects.get(id);
            // If object allocated
            if (obj) {
                // If alive, affect
                if (obj.alive) {
                    this.affectObject(obj);
                }
                // Otherwise, release
                else {
                    this.releaseObject(obj, false);
                }
                updated.push(obj);
            }
            else {
                const deadObj = this.deadObjects.get(id);
                // If object is dead, just add to the updated list
                if (deadObj) {
                    updated.push(deadObj);
                }
                // Otherwise, might need allocating
                else if (this.backend.getModel().exists(id)) {
                    updated.push(this.allocateObject(id));
                }
                else {
                    // If we got here, it's probably an object that is locally deleted receiving a late update, so do nothing.
                }
            }
        }
        this.deadObjects.clear();
        this.pumpStream('this', 'updated', updated);
    }

    private onModelOperation(moop: ModelOperation): void {
        const [type, id] = moop;

        if (type === $xcreate || type === $xrestore) {
            this.allocateObject(id);
        }
        else if (type === $xdelete) {
            this.releaseObject(id, true);
        }
        else if (type === $cset) {
            this.relations.notifyComponentSet(moop);
        }

        this.fireTriggerCycleConditionForOperation(moop);
    }

    public getDead(id: string): any {
        const inst = this.deadObjects.get(id);
        assert(!!inst, `ObjectManager.getDead: ${id} is not dead or never existed.`);
        return inst;
    }

    public flushAll() {
        for (const obj of this.objects.values()) {
            MemoStore.getMaybe(obj)?.clear();
        }
    }

    private createInstance({ variant, type }: ModelEntityInfo): Component {
        const ct = Component.describe(type);
        if (!ct) return null;
        return new (ct ? ct.type : Component)();
    }

    private allocateObject(id: string): any {
        const info = this.backend.getModel().getInfo(id);
        objLog.log('Allocating', info.type, id);
        const obj = this.createInstance(info);
        if (!obj) return;
        obj[props.Id] = id;
        obj[props.Gid] = `${this.backend.namespace}:${id}`;
        obj[props.Type] = info.type;
        obj[props.Host] = this.host;
        (obj as any).init();
        this.registerObject(obj);
        this.pumpStreams('allocated', [obj]);
        return obj;
    }

    private affectObject(obj: Component, flush?: boolean): void {
        obj[props.Ticker]++;
        if (flush) {
            MemoStore.clear(obj);
        }
        this.pumpStreams('affected', [obj]);
    }

    private registerObject(obj: AbstractObject): void {
        this.objects.set(obj.id, obj);
        if (obj instanceof Component) {
            const desc = Component.describe(obj);
            this.indexByType.add(desc.type, obj);
            for (const conv of desc.implements) {
                this.indexByConvention.add(conv, obj);
            }
        }
    }

    private releaseObject(idOrObj: string | Component, purgatorize: boolean): void {
        const obj = idOrObj instanceof Component ? idOrObj : this.objects.get(idOrObj);
        assert(!!obj, `ObjectManager.releaseObject: ${idOrObj} not found.`);
        objLog.log('Releasing', obj.type, obj.id);
        this.unregisterObject(obj);
        (obj as any).finalize();
        if (purgatorize) {
            this.deadObjects.set(obj.id, obj);
        }
        this.pumpStreams('released', [obj]);
    }

    private unregisterObject(obj: AbstractObject): void {
        this.objects.delete(obj.id);
        if (obj instanceof Component) {
            const desc = Component.describe(obj);
            this.indexByType.remove(desc.type, obj);
            for (const conv of desc.implements) {
                this.indexByConvention.remove(conv, obj);
            }
        }
    }

    private pumpStreams(stream: string, data: [Component, any?]) {
        this.pumpStream(data[0].id, stream, data);
        this.pumpStream(data[0].type, stream, data);
        this.pumpStream('component', stream, data);
    }

    private pumpStream(target: string, stream: string, data: any[]) {
        try {
            this.subscriptions.get(target, stream).forEach(x => x(data, stream));
        }
        catch (err) {
            Monitor.error(`Error pumping ${stream}`, err, { target, stream });
            errLog.error('Error pumping stream', target, stream);
            errLog.error(err);
        }
    }

    private fireTriggerCycleConditionForOperation(moop: ModelOperation): void {
        const ac = this.activeCycle;
        if (!ac) return;
        switch (moop[0]) {
            case $xcreate: return ac.emit('@created', moop[1]);
            case $xdelete: return ac.emit('@deleted', moop[1]);
            case $cappend: return ac.emit('@appended', moop[1]);
            case $ctruncate: return ac.emit('@truncated', moop[1]);
            case $cset: return ac.emit('@changesTo', moop[1], this.extractKeysFromSetOperation(moop));
        }
    }

    private extractKeysFromSetOperation(moop: ModelOperation) {
        const keys = [] as string[];
        for (let i = 2; i < moop.length; i += 2) {
            keys.push(moop[i]);
        }
        return keys;
    }
}


export interface ObjectUpdate {
    // create(type: 'map'): MapObject;
    // create(type: 'list'): ListObject;
    activate(tuple: ComponentTuple, useRefAsId?: boolean): Component;
    activateAll(tuples: ComponentTuple[], useRefAsId?: boolean): Component[];
    create<T extends Component>(type: ComponentType<T>): T;
    delete(component: Component, mode?: 'single' | 'cascade'): void;
    deleteAll(components: Component[], mode?: 'single' | 'cascade'): void;
    noop(): void;
    getMeta(prop: string): any;
    rolling(key: string): void;
    rolling(action: string, components: Component[]);
    set(prop: 'key', value: string): ObjectUpdate;
    set(prop: 'rolling', value: boolean): ObjectUpdate;
    set(prop: string, value: any): ObjectUpdate;
}

export type ObjectUpdateCallback<T> = Callback1<T, ObjectUpdate>;

type ObjectResolver = (id: string) => any;

class ObjectUpdateImpl implements ObjectUpdate {

    public depth = 0;
    public phase = 'mutation' as 'mutation' | 'reaction';

    constructor(private resolver: ObjectResolver, private backend: ObjectManagerBackend, private mutator: ModelMutator) {
    }

    public activate(tuple: ComponentTuple): Component {
        const [result] = this.activateAll([tuple]);
        return result;
    }

    public activateAll(tuples: ComponentTuple[], useRefAsId?: boolean): Component[] {
        // Create instances, connect tree, set states.  Do it in this order because we need to substitute the association property
        // references.

        const flatTree = this.flattenTupleTree(tuples);

        // Create instances
        const instsByRef = new Map<string, Component>();
        for (const [[type, ref]] of flatTree) {
            instsByRef.set(ref, this.instantiate(type, useRefAsId ? ref : null));
        }

        // Connect tree
        for (const [ct, pt] of flatTree) {
            if (!pt) continue;
            const parent = instsByRef.get(pt[1]);
            const child = instsByRef.get(ct[1]);
            this.mutator.componentAppend(parent.id, child.id);
        }

        // Hydrate states
        for (const [[_type, ref, state]] of flatTree) {
            const inst = instsByRef.get(ref);
            for (const prop of Object.keys(state)) {
                // If property is an association prop (starts with @), replace the ref in the value with the id of the appropriate instance
                const value = prop.startsWith('@') ? instsByRef.get(state[prop]?.toString())?.id ?? null : state[prop];
                this.mutator.componentSet(inst.id, prop, value);
            }
        }

        // Return only the top items
        return Array.from(instsByRef.values()).filter(x => !x.parent);
    }

    public create(type: any): any {
        return this.instantiate(type);
    }

    public delete(component: Component, mode?: 'single' | 'cascade'): void {

        if (mode === 'cascade') {
            Arr.rfor(component.flatten(), t => {
                if (component.alive) {
                    this.delete(t, mode);
                }
            });
        }

        if (component.parent) {
            component.removeSelf();
        }

        this.mutator.delete(component.id);
    }

    public deleteAll(components: Component[], mode?: 'single' | 'cascade'): void {

        for (const c of components) {
            this.delete(c, mode);
        }
    }

    public noop(): void {
        this.mutator.noop();
    }

    public get(prop: 'key' | 'rolling' | string): any {
        if (prop === 'key') {
            return this.mutator.key;
        }
        else if (prop === 'rolling') {
            return this.mutator.mode === 2;
        }
        else {
            return this.mutator.metadata[prop];
        }
    }

    public getMeta(prop: string) {
        return this.mutator.metadata[prop] ?? null;
    }

    public set(prop: 'key' | 'rolling' | string, value: any) {
        if (prop === 'key') {
            this.mutator.key = value;
        }
        else if (prop === 'rolling' && value) {
            this.mutator.mode = 2;
        }
        else {
            this.mutator.metadata[prop] = value;
        }
        return this;
    }

    public rolling(key: string);
    public rolling(action: string, components: Component[]);
    public rolling(keyOrAction: string, components?: Component[]) {
        if (Array.isArray(components)) {
            keyOrAction = [keyOrAction, ...components.map(x => x.id)].join(':');
        }
        this.set('rolling', true);
        this.set('key', keyOrAction);
    }

    private instantiate(type: any, explicitId?: string) {
        if (this.backend.shouldAllowCreates()) {
            const id = explicitId ?? this.backend.generateId();
            this.define(id, type);
            return this.resolver(id);
        }
        else {
            throw new ObjectManagerCreateNotAllowedError();
        }
    }

    private define(id: string, type: any) {
        switch (type) {
            // case 'map': return this.mutator.mapCreate(id);
            // case 'list': return this.mutator.listCreate(id);
            default: return this.mutator.componentCreate(id, Component.describe(type).name);
        }
    }

    private flattenTupleTree(tuples: ComponentTuple[], parent?: ComponentTuple) {
        const entries = [] as [ComponentTuple, ComponentTuple?][];
        for (const ct of tuples) {
            entries.push([ct, parent]);
            entries.push(...this.flattenTupleTree(ct[3], ct));
        }
        return entries;
    }
}

class ObjectHostImpl implements ObjectHost {

    constructor(
        private objects: ObjectManager,
        private filters: InterceptorRegister,
        private resolver: Resolver,
        private backend: ObjectManagerBackend,
        private onObjectCallback: VoidCallback1<Component>) {
    }

    public exists(id: string): boolean {
        return this.backend.getModel().exists(id);
    }

    public get(id: string): Component {
        return this.objects.get(id);
    }

    public getMaybe(id: string): Component | null {
        return this.objects.getMaybe(id);
    }

    public getParent(subject: Component): Component {
        const parentId = this.backend.getModel().getParent(subject.id);
        return !!parentId ? this.get(parentId) : null;
    }

    public getChildrenThisShouldBeRefactored(id: string): Readonly<Record<string, string>> {
        return this.backend.getModel().getChildren(id);
    }

    public getRelated(subject: Component, relationName: string): readonly Component[] {
        return this.objects.getRelated(subject, relationName);
    }

    public getState(subject: Component): Readonly<Record<string, string>> {
        return this.backend.getModel().getState(subject.id);
    }

    public getAttribute(subject: Component, key: string): any | null {
        return subject[props.Locals]?.get(key) ?? null;
    }

    public setAttribute(subject: Component, key: string, value: any | null): void {
        const locals = subject[props.Locals] ?? (subject[props.Locals] = new Map());
        if (value === null) {
            locals.delete(key);
        }
        else {
            locals.set(key, value);
        }
        this.onObjectCallback(subject);
    }

    public getProperty(subject: Component, key: string) {
        let value = this.backend.getModel().getStateValue(subject.id, key);
        value = this.filters.query(subject.type, key).reduce((t, f) => f.get(subject, t), value);
        return value;
    }

    public setProperty(subject: Component, key: string, value: any): void {
        value = this.filters.query(subject.type, key).reduce((t, f) => f.set(subject, t), value);
        this.getMutation().componentSet(subject.id, key, value);
    }

    public append(subject: Component, child: Component, before?: Component): void {
        this.getMutation().componentAppend(subject.id, child.id, before?.id);
    }

    public truncate(subject: Component, child: Component): void {
        this.getMutation().componentTruncate(subject.id, child.id);
    }

    public resolve<T>(identifier: interfaces.ServiceIdentifier<T>) {
        return this.resolver.call(identifier);
    }

    private getMutation(): ModelMutation {
        const m = this.backend.getMutation();
        assert(!!m, 'Cannot mutate object outside of update/mutation.');
        return m;
    }
}
